import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '../config/api';
import CameraCapture from '../components/CameraCapture';
import PhoneInput, { normalizePhone, phoneLocalDigits } from '../components/UI/PhoneInput';
import DocumentTile, { DOCUMENT_GRID } from '../components/UI/DocumentTile';

// Public page reached from the "please update your registration" email link.
// Mirrors the client self-registration form (stepper, same look) but pre-filled
// from the secure token, minus the account/password step. No login required.

const REQ_EMPTY = 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20';
const REQ_FILLED = '';
const NEUTRAL = 'border-gray-200 dark:border-slate-600 focus:border-blue-500 focus:ring-blue-500/20';
const baseInputCls = 'w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-500 rounded text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-400 focus:outline-none focus:ring-2 transition-colors';
const toneCls = (required, filled) => (required ? (filled ? REQ_FILLED : REQ_EMPTY) : NEUTRAL);
const inputCls = `${baseInputCls} ${NEUTRAL}`;

// The shared phone field (dial code + number), sized like the other fields.
const phoneBox = '!px-2 !py-1.5 !text-xs !bg-gray-50 dark:!bg-slate-700 !border';
const phoneTone = (filled) => (filled ? '!border-emerald-300 dark:!border-emerald-700/60' : '!border-rose-300 dark:!border-rose-700/60');

const Field = ({ label, name, value, onChange, type = 'text', required = true, placeholder, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">
      {label}
    </label>
    {children || (
      <input
        type={type}
        name={name}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        required={required}
        placeholder={placeholder}
        className={`${baseInputCls} ${toneCls(required, String(value ?? '').trim() !== '')}`}
      />
    )}
  </div>
);

const STEPS = [
  { key: 'personal', title: 'Personal' },
  { key: 'employment', title: 'Employment' },
  { key: 'banking', title: 'Banking' },
  { key: 'documents', title: 'Documents' },
];

// PDF documents (the client photo is camera-only, handled separately).
const DOC_DEFS = [
  { name: 'identificationFile', type: 'Identification', label: 'Identification document' },
  { name: 'payslipFile', type: 'Payslip', label: 'Latest payslip' },
  { name: 'bankStatementFile', type: 'Bank Statement', label: 'Three months bank statement' },
  { name: 'proofOfResidenceFile', type: 'Proof Of Residence', label: 'Proof of residence' },
];
const PHOTO_TYPE = 'Online Client Photo';

const REQUIRED_TEXT = {
  fullName: 'Full name', gender: 'Gender', idPassportNo: 'ID/Passport No',
  postalAddress: 'Postal address', postalTelNo: 'Phone number', erfNo: 'ERF no',
  streetName: 'Street name', suburb: 'Location/Suburb', city: 'City/Town', country: 'Country',
  maritalStatus: 'Marital status', occupation: 'Occupation', employerName: 'Employer name',
  employerPhone: 'Employer phone', employerAddress: 'Employer address', payslipNumber: 'Payslip number',
  bankName: 'Bank name', branchCode: 'Branch code', accountNumber: 'Account number', accountType: 'Account type',
};

const EMPTY = {
  fullName: '', gender: '', idPassportNo: '', postalAddress: '', postalTelNo: '',
  erfNo: '', streetName: '', suburb: '', city: '', country: '', maritalStatus: '',
  occupation: '', salary: '', employerName: '', employerPhone: '', employerAddress: '', payslipNumber: '',
  bankName: '', branchCode: '', accountNumber: '', accountType: '',
};

const MAX_BYTES = 10 * 1024 * 1024;

const UpdateRegistration = () => {
  const token = useMemo(() => new URLSearchParams(window.location.search).get('token') || '', []);
  const tenantSlug = useMemo(() => new URLSearchParams(window.location.search).get('tenantSlug') || '', []);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [existingDocs, setExistingDocs] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState('');
  const [step, setStep] = useState(0);
  const [agreedTerms, setAgreedTerms] = useState(false);

  const setField = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  useEffect(() => {
    if (!token) { setLoadError('No token provided'); setLoading(false); return; }
    (async () => {
      try {
        const res = await axios.get(apiUrl(`/api/clients/registration/by-token/${encodeURIComponent(token)}`), {
          params: tenantSlug ? { tenantSlug } : {},
        });
        const d = res.data || {};
        setEmail(d.email || '');
        setNote(d.note || '');
        setExistingDocs(Array.isArray(d.existingDocuments) ? d.existingDocuments : []);
        const fields = { ...EMPTY, ...(d.fields || {}) };
        fields.postalTelNo = normalizePhone(fields.postalTelNo) || fields.postalTelNo;
        fields.employerPhone = normalizePhone(fields.employerPhone) || fields.employerPhone;
        setForm(fields);
      } catch (err) {
        setLoadError(err?.response?.data?.error || 'This update link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token, tenantSlug]);

  // PDF picker with type + size validation.
  const onPickPdf = (name, f) => {
    if (f && f.type !== 'application/pdf') { setError('Only PDF files are allowed for documents (the photo is taken with your camera).'); return; }
    if (f && f.size > MAX_BYTES) { setError('Each document must be 10 MB or smaller.'); return; }
    setError('');
    setFiles((p) => ({ ...p, [name]: f }));
  };

  const validateStep = () => {
    setError('');
    const cur = STEPS[step].key;
    if (cur === 'personal') {
      for (const k of ['fullName', 'gender', 'idPassportNo', 'postalAddress', 'postalTelNo', 'erfNo', 'streetName', 'suburb', 'city', 'country', 'maritalStatus']) {
        if (!String(form[k] || '').trim()) { setError(`Please fill in: ${REQUIRED_TEXT[k]}`); return false; }
      }
      if (phoneLocalDigits(form.postalTelNo).length < 8) { setError('Please enter your full phone number.'); return false; }
      return true;
    }
    if (cur === 'employment') {
      for (const k of ['occupation', 'employerName', 'employerPhone', 'employerAddress', 'payslipNumber']) {
        if (!String(form[k] || '').trim()) { setError(`Please fill in: ${REQUIRED_TEXT[k]}`); return false; }
      }
      if (phoneLocalDigits(form.employerPhone).length < 8) { setError("Please enter the employer's full phone number."); return false; }
      if (!(Number(form.salary) > 0)) { setError('Please enter a valid monthly salary (greater than 0).'); return false; }
      return true;
    }
    if (cur === 'banking') {
      for (const k of ['bankName', 'branchCode', 'accountNumber', 'accountType']) {
        if (!String(form[k] || '').trim()) { setError(`Please fill in: ${REQUIRED_TEXT[k]}`); return false; }
      }
      return true;
    }
    return true; // documents are optional — keep existing if not re-uploaded
  };

  const next = () => { if (validateStep()) { setStep((s) => Math.min(s + 1, STEPS.length - 1)); setError(''); } };
  const prev = () => { setStep((s) => Math.max(s - 1, 0)); setError(''); };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    setSubmitting(true);
    setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
      if (tenantSlug) fd.append('tenantSlug', tenantSlug);
      [...DOC_DEFS.map((d) => d.name), 'whatsAppPhotoFile'].forEach((name) => { if (files[name]) fd.append(name, files[name]); });
      const res = await axios.put(apiUrl(`/api/clients/registration/by-token/${encodeURIComponent(token)}`), fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDone(res.data?.message || 'Thank you — your updated details have been submitted for review.');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to submit your updated details. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Full-page shell for the loading / error / done states.
  const StatusScreen = ({ accent = 'blue', icon, heading, children }) => (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden">
        <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />
        <div className="px-6 py-8 text-center">
          {icon && <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-blue-50 dark:bg-slate-700 flex items-center justify-center text-2xl">{icon}</div>}
          <h1 className={`text-xl font-extrabold mb-2 ${accent === 'red' ? 'text-red-600 dark:text-red-400' : 'text-blue-800 dark:text-blue-300'}`}>{heading}</h1>
          {children}
        </div>
      </div>
    </div>
  );

  if (loading) return <StatusScreen heading="Loading your registration…" icon="⏳" />;
  if (loadError) {
    return (
      <StatusScreen accent="red" icon="⚠️" heading="We couldn't open this link">
        <p className="text-sm text-gray-600 dark:text-slate-300">{loadError}</p>
        <p className="text-xs text-gray-500 dark:text-slate-400 mt-3">Please use the secure link from the email we sent you. If it has expired, contact us to request a new one.</p>
      </StatusScreen>
    );
  }
  if (done) {
    return (
      <StatusScreen icon="✅" heading="Submitted">
        <p className="text-sm text-gray-700 dark:text-slate-200">{done}</p>
        <p className="text-xs text-gray-500 dark:text-slate-400 mt-3">Our team will review your details and approve your account. You'll be able to sign in once approved.</p>
      </StatusScreen>
    );
  }

  const StepHeader = () => (
    <div className="flex items-center gap-1.5 sm:gap-2 mb-5">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.key}>
          {i > 0 && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 text-gray-300 dark:text-slate-600">
              <path d="m9 18 6-6-6-6" />
            </svg>
          )}
          <div
            className={`flex-1 text-center px-2 sm:px-3 py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors ${
              i === step
                ? 'bg-blue-600 text-white shadow-sm'
                : i < step
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800/40'
                : 'bg-gray-100 text-gray-500 border border-gray-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600'
            }`}
          >
            {i < step && <span className="mr-1">✓</span>}
            {s.title}
          </div>
        </React.Fragment>
      ))}
    </div>
  );

  const cur = STEPS[step].key;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden">
        <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />
        <div className="px-6 py-6 sm:px-8 sm:py-7">
          <h1 className="text-2xl font-extrabold text-blue-800 dark:text-blue-300 text-center mb-1">Update your registration</h1>
          <p className="text-sm text-gray-500 dark:text-slate-400 text-center mb-5">
            {email ? <>For <strong className="text-gray-700 dark:text-slate-200">{email}</strong>. </> : null}
            Review the details below, make the requested changes, and resubmit for approval.
          </p>

          {note && (
            <div className="mb-5 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 dark:border-amber-500 text-amber-900 dark:text-amber-200">
              <div className="text-sm font-bold uppercase tracking-wide mb-1">What needs to be updated</div>
              <div className="text-sm whitespace-pre-wrap">{note}</div>
            </div>
          )}

          <StepHeader />

          {error && (
            <div className="mb-4 p-3 rounded border border-red-200 bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800/40 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); if (step < STEPS.length - 1) next(); else onSubmit(e); }} className="space-y-4">
            {/* ── Personal ── */}
            {cur === 'personal' && (
              <>
                <Field label="Full name" name="fullName" value={form.fullName} onChange={setField} placeholder="First Middle Last" />
                <Field label="Gender" name="gender" value={form.gender} onChange={setField}>
                  <select value={form.gender} onChange={(e) => setField('gender', e.target.value)} required className={`${baseInputCls} ${toneCls(true, form.gender !== '')}`}>
                    <option value="">Select…</option><option value="Male">Male</option><option value="Female">Female</option><option value="Other">Other</option>
                  </select>
                </Field>
                <Field label="ID / Passport No" name="idPassportNo" value={form.idPassportNo} onChange={setField} />
                <Field label="Postal address" name="postalAddress" value={form.postalAddress} onChange={setField} />
                <Field label="Phone number" name="postalTelNo">
                  <PhoneInput value={form.postalTelNo} onChange={(v) => setField('postalTelNo', v)} required
                    selectClassName={`${phoneBox} ${phoneTone(phoneLocalDigits(form.postalTelNo).length >= 8)}`}
                    inputClassName={`${phoneBox} ${phoneTone(phoneLocalDigits(form.postalTelNo).length >= 8)}`} />
                </Field>
                <div>
                  <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Residential address</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><span className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">ERF no</span><input type="text" value={form.erfNo} onChange={(e) => setField('erfNo', e.target.value)} required className={inputCls} /></div>
                    <div><span className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Street name</span><input type="text" value={form.streetName} onChange={(e) => setField('streetName', e.target.value)} required className={inputCls} /></div>
                    <div><span className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Location / Suburb</span><input type="text" value={form.suburb} onChange={(e) => setField('suburb', e.target.value)} required className={inputCls} /></div>
                    <div><span className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">City / Town</span><input type="text" value={form.city} onChange={(e) => setField('city', e.target.value)} required className={inputCls} /></div>
                    <div className="sm:col-span-2"><span className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Country</span><input type="text" value={form.country} onChange={(e) => setField('country', e.target.value)} required className={inputCls} /></div>
                  </div>
                </div>
                <Field label="Marital status" name="maritalStatus" value={form.maritalStatus} onChange={setField}>
                  <select value={form.maritalStatus} onChange={(e) => setField('maritalStatus', e.target.value)} required className={`${baseInputCls} ${toneCls(true, form.maritalStatus !== '')}`}>
                    <option value="">Select…</option><option value="Single">Single</option><option value="Married">Married</option><option value="Divorced">Divorced</option><option value="Widowed">Widowed</option>
                  </select>
                </Field>
              </>
            )}

            {/* ── Employment ── */}
            {cur === 'employment' && (
              <>
                <Field label="Occupation" name="occupation" value={form.occupation} onChange={setField} />
                <Field label="Salary (N$)" name="salary" type="number" value={form.salary} onChange={setField} placeholder="Monthly gross salary" />
                <Field label="Employer name" name="employerName" value={form.employerName} onChange={setField} />
                <Field label="Employer phone" name="employerPhone">
                  <PhoneInput value={form.employerPhone} onChange={(v) => setField('employerPhone', v)} required
                    selectClassName={`${phoneBox} ${phoneTone(phoneLocalDigits(form.employerPhone).length >= 8)}`}
                    inputClassName={`${phoneBox} ${phoneTone(phoneLocalDigits(form.employerPhone).length >= 8)}`} />
                </Field>
                <Field label="Employer address" name="employerAddress" value={form.employerAddress} onChange={setField} />
                <Field label="Payslip number" name="payslipNumber" value={form.payslipNumber} onChange={setField} />
              </>
            )}

            {/* ── Banking ── */}
            {cur === 'banking' && (
              <>
                <Field label="Bank name" name="bankName" value={form.bankName} onChange={setField} />
                <Field label="Branch code" name="branchCode" value={form.branchCode} onChange={setField} />
                <Field label="Account number" name="accountNumber" value={form.accountNumber} onChange={setField} />
                <Field label="Account type" name="accountType" value={form.accountType} onChange={setField}>
                  <select value={form.accountType} onChange={(e) => setField('accountType', e.target.value)} required className={`${baseInputCls} ${toneCls(true, form.accountType !== '')}`}>
                    <option value="">Select…</option><option value="Cheque">Cheque</option><option value="Savings">Savings</option><option value="Transmission">Transmission</option>
                  </select>
                </Field>
              </>
            )}

            {/* ── Documents ── */}
            {cur === 'documents' && (
              <>
                <div className="p-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-sm font-medium">
                  Your documents are already on file. Only re-upload a document if you need to replace it — leave it blank to keep what you submitted. <strong>PDFs only, max 10&nbsp;MB each.</strong> The client photo is taken with your camera.
                </div>
                <div className={DOCUMENT_GRID}>
                  {DOC_DEFS.map(({ name, type, label }) => (
                    <DocumentTile
                      key={name}
                      type={type}
                      label={label}
                      hint="PDF · max 10 MB"
                      doc={existingDocs.includes(type) ? { file_name: 'On file · leave to keep' } : null}
                      file={files[name]}
                      onPick={(f) => onPickPdf(name, f)}
                      onRemove={() => setFiles((p) => ({ ...p, [name]: null }))}
                      required
                    />
                  ))}

                  {/* Client photo — camera only */}
                  <div className="rounded-lg border border-gray-200 dark:border-slate-600 p-3 bg-gray-50 dark:bg-slate-700/40">
                    <div className="flex items-center justify-between mb-1.5 gap-2">
                      <span className="text-sm font-medium text-gray-800 dark:text-slate-200">Client photo</span>
                      {existingDocs.includes(PHOTO_TYPE) && <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 whitespace-nowrap">✓ On file</span>}
                    </div>
                    <div onClick={(e) => e.preventDefault()}>
                      <CameraCapture onPhotoCapture={(file) => setFiles((p) => ({ ...p, whatsAppPhotoFile: file }))} buttonText="Take selfie" />
                    </div>
                    {files.whatsAppPhotoFile
                      ? <p className="mt-1 text-[11px] text-green-600 dark:text-green-400">✓ New photo captured</p>
                      : <p className="mt-1 text-[11px] text-gray-400 dark:text-slate-500">Taken with your camera · leave to keep</p>}
                  </div>
                </div>
              </>
            )}

            {/* Terms & Conditions agreement — required on the final step */}
            {step === STEPS.length - 1 && (
              <label className="flex items-start gap-2.5 text-sm text-gray-700 dark:text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedTerms}
                  onChange={(e) => setAgreedTerms(e.target.checked)}
                  required
                  disabled={submitting}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                />
                <span>
                  I have read and agree to the{' '}
                  <Link
                    to={tenantSlug ? `/${tenantSlug}/terms` : '/terms'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 dark:text-blue-300 font-semibold hover:underline"
                  >
                    Terms &amp; Conditions
                  </Link>
                  , including the processing and sharing of my information described in them.
                </span>
              </label>
            )}

            {/* Nav */}
            <div className="flex items-center justify-between pt-2 gap-2">
              <button type="button" onClick={prev} disabled={step === 0 || submitting}
                className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-40 text-sm font-semibold">
                ← Previous
              </button>
              {step < STEPS.length - 1 ? (
                <button type="button" onClick={next} disabled={submitting}
                  className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-60">
                  Next →
                </button>
              ) : (
                <button type="submit" disabled={submitting || !agreedTerms}
                  title={!agreedTerms ? 'Please agree to the Terms & Conditions first' : undefined}
                  className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-60">
                  {submitting ? 'Submitting…' : 'Submit updated details'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdateRegistration;
